import { useMemo, useState } from "react";

/**
 * Shared filtering / sorting hook for "Événements à venir" lists.
 * - Filters input array by alert/date range/search.
 * - Sorts result by `event_date` ASC (closest events first).
 * - Returns the visible subset (limited to 6 unless `expanded`) and the
 *   stateful controls so the caller can render the filter bar.
 *
 * The hook is intentionally framework-agnostic for the underlying data
 * shape: events must expose `event_date` (YYYY-MM-DD), `title`,
 * `host_names`, `code`, `logistics_alert`.
 */
const COMPACT_PAGE = 6;

export default function useEventFilters(events) {
  const [search, setSearch] = useState("");
  const [alertOnly, setAlertOnly] = useState(false);
  // 'week' | 'month' | 'all'
  const [range, setRange] = useState("all");
  const [expanded, setExpanded] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const now = new Date();
    const endOfWeek = new Date(now);
    endOfWeek.setDate(now.getDate() + 7);
    const endOfMonth = new Date(now);
    endOfMonth.setDate(now.getDate() + 30);

    return events
      .filter((e) => {
        if (alertOnly && !e.logistics_alert) return false;
        if (q) {
          const hay = `${e.title || ""} ${e.host_names || ""} ${e.code || ""}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        if (range !== "all" && e.event_date) {
          const d = new Date(e.event_date);
          if (Number.isNaN(d.getTime())) return false;
          const max = range === "week" ? endOfWeek : endOfMonth;
          if (d > max) return false;
        }
        return true;
      })
      .sort((a, b) => {
        // event_date ASC, then created_at DESC as tiebreaker so same-day events keep a stable order.
        const da = a.event_date || "";
        const db = b.event_date || "";
        if (da !== db) return da < db ? -1 : 1;
        const ca = a.created_at || "";
        const cb = b.created_at || "";
        return ca < cb ? 1 : -1;
      });
  }, [events, search, alertOnly, range]);

  const visible = expanded ? filtered : filtered.slice(0, COMPACT_PAGE);
  const hiddenCount = Math.max(0, filtered.length - visible.length);

  return {
    search, setSearch,
    alertOnly, setAlertOnly,
    range, setRange,
    expanded, setExpanded,
    filtered,
    visible,
    hiddenCount,
    pageSize: COMPACT_PAGE,
  };
}
